import React from 'react';
import { CosmosLogoIcon } from './icons';

interface LoadingScreenProps {
    message?: string;
    showLogo?: boolean;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
    message = "Loading...", 
    showLogo = true 
}) => {
    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-b from-[rgba(249,247,243,0.9)] to-[rgba(249,247,243,0.9)] bg-white">
            <div className="flex flex-col items-center gap-6">
                {showLogo && (
                    <div className="flex items-center justify-center w-16 h-16 p-2 rounded-2xl bg-primary-100">
                        <CosmosLogoIcon />
                    </div>
                )}
                
                {/* Loading spinner */}
                <div className="flex items-center gap-3">
                    <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-og-600 rounded-full animate-spin" role="status" aria-label="Loading"></div>
                    <span className="text-grayscale-1200 font-grotesk text-sm" role="status" aria-live="polite">{message}</span>
                </div>
            </div>
        </div>
    );
};

interface ChatLoadingSkeletonProps {}

export const ChatLoadingSkeleton: React.FC<ChatLoadingSkeletonProps> = () => {
    return (
        <div className="flex-1 flex flex-col self-stretch rounded-2xl bg-white shadow-custom-light p-6 gap-5 animate-pulse">
            {/* Header skeleton */}
            <div className="p-4 flex-col justify-end items-end gap-4 self-stretch rounded-2xl border border-grayscale-200">
                <div className="flex justify-between items-center self-stretch">
                    <div className="h-6 bg-grayscale-200 rounded w-64"></div>
                    <div className="h-10 bg-grayscale-200 rounded-full w-32"></div>
                </div>
            </div>

            {/* Messages skeleton */}
            <div className="flex-1 flex flex-col gap-4">
                {/* User message skeleton */}
                <div className="self-end max-w-[80%]">
                    <div className="p-4 rounded-2xl bg-grayscale-100">
                        <div className="h-4 bg-grayscale-200 rounded w-48 mb-2"></div>
                        <div className="h-4 bg-grayscale-200 rounded w-32"></div>
                    </div>
                </div>

                {/* AI message skeleton */}
                <div className="self-start max-w-[80%]">
                    <div className="p-4 rounded-2xl bg-grayscale-100">
                        <div className="h-4 bg-grayscale-200 rounded w-64 mb-2"></div>
                        <div className="h-4 bg-grayscale-200 rounded w-48 mb-2"></div>
                        <div className="h-4 bg-grayscale-200 rounded w-56"></div>
                    </div>
                </div>

                {/* Another user message skeleton */}
                <div className="self-end max-w-[80%]">
                    <div className="p-4 rounded-2xl bg-grayscale-100">
                        <div className="h-4 bg-grayscale-200 rounded w-40"></div>
                    </div>
                </div>
            </div>

            {/* Input skeleton */}
            <div className="flex items-center gap-2 p-3 rounded-2xl border border-grayscale-200">
                <div className="flex-1 h-6 bg-grayscale-200 rounded"></div>
                <div className="w-8 h-8 bg-grayscale-200 rounded-full"></div>
            </div>
        </div>
    );
};

interface InboxLoadingSkeletonProps {}

export const InboxLoadingSkeleton: React.FC<InboxLoadingSkeletonProps> = () => {
    return (
        <div className="flex-1 flex flex-col self-stretch rounded-2xl bg-white shadow-custom-light p-6 gap-5 animate-pulse">
            {/* Header skeleton */}
            <div className="p-4 flex-col justify-end items-end gap-4 self-stretch rounded-2xl border border-grayscale-200">
                <div className="flex justify-between items-center self-stretch">
                    <div className="h-6 bg-grayscale-200 rounded w-48"></div>
                    <div className="h-10 bg-grayscale-200 rounded-full w-32"></div>
                </div>
            </div>

            {/* Messages skeleton */}
            <div className="flex-1 flex flex-col gap-4">
                {/* University message skeleton */}
                <div className="self-start max-w-[80%]">
                    <div className="p-4 rounded-2xl bg-grayscale-100">
                        <div className="h-4 bg-grayscale-200 rounded w-64 mb-2"></div>
                        <div className="h-4 bg-grayscale-200 rounded w-48 mb-2"></div>
                        <div className="h-4 bg-grayscale-200 rounded w-56 mb-2"></div>
                        <div className="h-4 bg-grayscale-200 rounded w-40"></div>
                    </div>
                </div>

                {/* User message skeleton */}
                <div className="self-end max-w-[80%]">
                    <div className="p-4 rounded-2xl bg-grayscale-100">
                        <div className="h-4 bg-grayscale-200 rounded w-52 mb-2"></div>
                        <div className="h-4 bg-grayscale-200 rounded w-36"></div>
                    </div>
                </div>
            </div>

            {/* Input skeleton */}
            <div className="flex items-center gap-2 p-3 rounded-2xl border border-grayscale-200">
                <div className="flex-1 h-6 bg-grayscale-200 rounded"></div>
                <div className="w-8 h-8 bg-grayscale-200 rounded-full"></div>
            </div>
        </div>
    );
};
